import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Send as SendIcon, MessageCircle, ShieldCheck, Star, ArrowLeft, Ban, Check as CheckIcon, CheckCheck } from "lucide-react";
import UserAvatar from "@/components/UserAvatar";
import EmojiPickerBar from "@/components/EmojiPickerBar";

function initial(name) {
  const s = (name || "?").trim();
  return s.charAt(0).toUpperCase();
}

function ProfileView({ profile, blocked, onStartDm, onToggleBlock, blockBusy }) {
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4">
        <UserAvatar
          path={profile.profile_picture_path}
          size={72}
          className={`${profile.role === "admin" ? "bg-slate-900 text-white" : profile.is_master ? "bg-gradient-to-br from-amber-400 to-amber-600 text-white" : "bg-slate-200 text-slate-700"} text-2xl font-bold`}
          fallback={profile.role === "admin" ? <ShieldCheck size={28} /> : profile.is_master ? <Star size={22} className="fill-white" /> : initial(profile.name || profile.email)}
        />
        <div className="min-w-0 flex-1">
          <div className="font-display font-semibold text-lg truncate">{profile.name || (profile.email || "").split("@")[0]}</div>
          <div className="mt-0.5 flex flex-wrap gap-1.5">
            {profile.role === "admin" && <span className="text-[9px] font-mono-plex tracking-widest uppercase px-2 py-0.5 rounded bg-slate-900 text-white">ADMIN</span>}
            {profile.is_master && profile.role !== "admin" && <span className="text-[9px] font-mono-plex tracking-widest uppercase px-2 py-0.5 rounded bg-amber-500 text-white">MASTER</span>}
            {blocked && <span className="text-[9px] font-mono-plex tracking-widest uppercase px-2 py-0.5 rounded bg-rose-500 text-white">BLOCKED</span>}
          </div>
        </div>
      </div>
      <dl className="space-y-1.5 text-sm">
        {profile.telegram_username && (
          <div className="flex justify-between gap-3">
            <dt className="text-[10px] font-mono-plex tracking-widest uppercase text-slate-500">Telegram</dt>
            <dd className="font-mono-plex text-xs truncate">@{profile.telegram_username}</dd>
          </div>
        )}
        {profile.state && (
          <div className="flex justify-between gap-3">
            <dt className="text-[10px] font-mono-plex tracking-widest uppercase text-slate-500">State</dt>
            <dd className="text-xs truncate">{profile.state}</dd>
          </div>
        )}
        {profile.trading_experience && (
          <div className="flex justify-between gap-3">
            <dt className="text-[10px] font-mono-plex tracking-widest uppercase text-slate-500">Experience</dt>
            <dd className="text-xs truncate">{profile.trading_experience}</dd>
          </div>
        )}
      </dl>
      {onStartDm && !blocked && (
        <button
          data-testid="user-profile-start-dm"
          onClick={onStartDm}
          className="w-full inline-flex items-center justify-center gap-2 bg-[#00b4ff] hover:bg-[#0090cc] text-white font-semibold text-sm px-4 py-2.5 rounded-full"
          style={{ transition: "background-color 0.2s ease" }}
        >
          <MessageCircle size={14} /> Send private message
        </button>
      )}
      {onToggleBlock && profile.role !== "admin" && (
        <button
          data-testid="user-profile-toggle-block"
          onClick={onToggleBlock}
          disabled={blockBusy}
          className={`w-full inline-flex items-center justify-center gap-2 font-semibold text-sm px-4 py-2.5 rounded-full disabled:opacity-60 ${blocked ? "bg-white border border-rose-200 text-rose-600 hover:bg-rose-50" : "bg-white border border-slate-200 text-slate-600 hover:border-rose-300 hover:text-rose-600"}`}
          style={{ transition: "background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease" }}
        >
          <Ban size={14} /> {blocked ? "Unblock user" : "Block user"}
        </button>
      )}
    </div>
  );
}

function DmThreadView({ peer, meId, onBack }) {
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [peerTyping, setPeerTyping] = useState(false);
  const [iBlocked, setIBlocked] = useState(false);
  const [theyBlocked, setTheyBlocked] = useState(false);
  const listRef = useRef(null);
  const inputRef = useRef(null);
  const pollRef = useRef(null);
  const typingSentAtRef = useRef(0);

  const load = async () => {
    try {
      const { data } = await api.get(`/user-dm/${peer.id}`);
      setMessages(data.messages || []);
      setPeerTyping(!!data.peer_typing);
      setIBlocked(!!data.i_blocked);
      setTheyBlocked(!!data.they_blocked);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load thread");
    } finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    pollRef.current = setInterval(load, 4000);
    return () => clearInterval(pollRef.current);
    // eslint-disable-next-line
  }, [peer.id]);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, peerTyping]);

  const sendTypingPing = () => {
    const now = Date.now();
    if (now - typingSentAtRef.current < 2500) return;
    typingSentAtRef.current = now;
    api.post(`/user-dm/${peer.id}/typing`).catch(() => {});
  };

  const send = async (e) => {
    e.preventDefault();
    const text = draft.trim();
    if (!text || sending) return;
    setSending(true);
    try {
      const { data } = await api.post(`/user-dm/${peer.id}`, { text });
      setMessages((prev) => [...prev, data]);
      setDraft("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send");
    } finally { setSending(false); }
  };

  const insertEmoji = (emoji) => {
    const el = inputRef.current;
    if (!el) { setDraft((d) => d + emoji); return; }
    const s = el.selectionStart ?? draft.length;
    const e = el.selectionEnd ?? draft.length;
    const next = draft.slice(0, s) + emoji + draft.slice(e);
    setDraft(next);
    requestAnimationFrame(() => {
      el.focus();
      const pos = s + emoji.length;
      el.setSelectionRange(pos, pos);
    });
  };

  const composerDisabled = iBlocked || theyBlocked;
  const blockedMsg = iBlocked ? "You blocked this user — unblock to resume." : theyBlocked ? "You can't message this user." : "";

  return (
    <div className="flex flex-col h-[70vh] max-h-[70vh] w-full min-w-0 overflow-hidden">
      <div className="flex items-center gap-2 pb-3 border-b border-slate-100 min-w-0">
        {onBack && (
          <button onClick={onBack} data-testid="dm-thread-back" className="p-1.5 rounded-md hover:bg-slate-100" aria-label="Back">
            <ArrowLeft size={16} />
          </button>
        )}
        <UserAvatar
          path={peer.profile_picture_path}
          size={32}
          className={`${peer.role === "admin" ? "bg-slate-900 text-white" : peer.is_master ? "bg-gradient-to-br from-amber-400 to-amber-600 text-white" : "bg-slate-200 text-slate-700"} text-xs font-bold`}
          fallback={peer.role === "admin" ? <ShieldCheck size={14} /> : peer.is_master ? <Star size={12} className="fill-white" /> : initial(peer.name || peer.email)}
        />
        <div className="min-w-0 flex-1">
          <div className="font-display font-semibold text-sm truncate">{peer.name || (peer.email || "").split("@")[0]}</div>
          <div data-testid="dm-thread-status" className="text-[10px] font-mono-plex tracking-widest uppercase text-slate-500 truncate">
            {peerTyping ? <span className="text-[#00b4ff]">typing…</span> : "private message"}
          </div>
        </div>
      </div>
      <div ref={listRef} data-testid="dm-thread-messages" className="flex-1 overflow-y-auto py-3 space-y-2" style={{ overscrollBehavior: "contain" }}>
        {loading ? (
          <div className="h-full grid place-items-center text-slate-400 text-sm">Loading…</div>
        ) : messages.length === 0 ? (
          <div className="h-full grid place-items-center text-center text-slate-400 text-sm">
            <div>
              <MessageCircle size={28} className="mx-auto opacity-30" />
              <div className="mt-2 font-mono-plex tracking-widest uppercase text-[10px]">No messages yet</div>
              <div className="mt-0.5 text-xs">Kirim mesej pertama untuk mula chat.</div>
            </div>
          </div>
        ) : messages.map((m) => {
          const mine = m.sender_id === meId;
          const seen = !!m.read_at;
          return (
            <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`dm-msg-${m.id}`}>
              <div className={`flex flex-col ${mine ? "items-end" : "items-start"} max-w-[75%]`}>
                <div className={`px-3.5 py-2 text-sm leading-relaxed whitespace-pre-wrap break-words rounded-2xl ${mine ? "bg-[#00b4ff] text-white rounded-br-md" : "bg-slate-100 text-slate-900 rounded-bl-md"}`} style={{ overflowWrap: "anywhere" }}>
                  {m.text}
                </div>
                {mine && (
                  <div className="mt-0.5 flex items-center gap-1 text-[9px] font-mono-plex text-slate-400 pr-0.5" data-testid={`dm-msg-ticks-${m.id}`}>
                    {seen ? <><CheckCheck size={11} className="text-[#00b4ff]" /><span className="text-[#00b4ff]">seen</span></> : <><CheckIcon size={11} /><span>sent</span></>}
                  </div>
                )}
              </div>
            </div>
          );
        })}
        {peerTyping && messages.length > 0 && (
          <div className="flex justify-start" data-testid="dm-typing-bubble">
            <div className="px-3.5 py-2 bg-slate-100 rounded-2xl rounded-bl-md inline-flex gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: "0ms" }} />
              <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: "120ms" }} />
              <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: "240ms" }} />
            </div>
          </div>
        )}
      </div>
      <form onSubmit={send} className="pt-3 border-t border-slate-100 space-y-2 min-w-0">
        {composerDisabled ? (
          <div data-testid="dm-thread-blocked-banner" className="text-xs font-mono-plex tracking-wide text-rose-600 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2 text-center">
            {blockedMsg}
          </div>
        ) : (
          <EmojiPickerBar onPick={insertEmoji} />
        )}
        <div className="flex items-end gap-2 min-w-0">
          <textarea
            ref={inputRef}
            data-testid="dm-thread-input"
            value={draft}
            onChange={(e) => { setDraft(e.target.value); if (!composerDisabled) sendTypingPing(); }}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(e); } }}
            placeholder={composerDisabled ? "Messaging disabled" : "Ketik mesej…"}
            maxLength={2000}
            rows={1}
            disabled={composerDisabled}
            className="flex-1 min-w-0 resize-none max-h-24 px-3 py-2.5 rounded-2xl bg-slate-100 focus:bg-white border border-transparent focus:border-[#00b4ff] focus:outline-none text-sm leading-relaxed disabled:opacity-60 disabled:cursor-not-allowed"
            style={{ transition: "background-color 0.2s ease, border-color 0.2s ease" }}
          />
          <button
            data-testid="dm-thread-send"
            type="submit"
            disabled={!draft.trim() || sending || composerDisabled}
            className="grid place-items-center w-10 h-10 rounded-full bg-[#00b4ff] hover:bg-[#0090cc] disabled:opacity-50 disabled:cursor-not-allowed text-white shrink-0"
            style={{ transition: "background-color 0.2s ease" }}
            aria-label="Send"
          >
            <SendIcon size={16} className="translate-x-[1px] -translate-y-[1px]" />
          </button>
        </div>
      </form>
    </div>
  );
}

/**
 * Dialog showing a user's public profile with an inline "start DM" flow.
 * Two modes: "profile" and "dm". `initialMode` decides which view to show first.
 */
export default function UserProfileDialog({ userId, open, onOpenChange, initialMode = "profile" }) {
  const { user: me } = useAuth();
  const [profile, setProfile] = useState(null);
  const [mode, setMode] = useState(initialMode);
  const [loading, setLoading] = useState(false);
  const [blocked, setBlocked] = useState(false);
  const [blockBusy, setBlockBusy] = useState(false);

  useEffect(() => {
    if (!open || !userId) return;
    setMode(initialMode);
    setLoading(true);
    Promise.all([
      api.get(`/users/${userId}/profile`),
      api.get(`/user-blocks`),
    ]).then(([p, b]) => {
      setProfile(p.data);
      const list = (b.data.items || []).map((row) => row.blocked_id);
      setBlocked(list.includes(userId));
    }).catch((err) => toast.error(err.response?.data?.detail || "Failed to load profile"))
      .finally(() => setLoading(false));
  }, [open, userId, initialMode]);

  const isSelf = profile && me && profile.id === me.id;

  const toggleBlock = async () => {
    if (!profile) return;
    setBlockBusy(true);
    try {
      if (blocked) {
        await api.delete(`/user-blocks/${profile.id}`);
        setBlocked(false);
        toast.success("User unblocked");
      } else {
        await api.post(`/user-blocks/${profile.id}`);
        setBlocked(true);
        toast.success("User blocked");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally { setBlockBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md max-w-[calc(100vw-2rem)] overflow-hidden" data-testid="user-profile-dialog">
        <DialogHeader>
          <DialogTitle className="font-display sr-only">User profile</DialogTitle>
          <DialogDescription className="sr-only">User profile and private message thread</DialogDescription>
        </DialogHeader>
        {loading || !profile ? (
          <div className="py-8 text-center text-slate-400 text-sm">Loading…</div>
        ) : mode === "dm" && !isSelf ? (
          <DmThreadView peer={profile} meId={me.id} onBack={() => setMode("profile")} />
        ) : (
          <ProfileView
            profile={profile}
            blocked={blocked}
            onStartDm={isSelf ? null : () => setMode("dm")}
            onToggleBlock={isSelf ? null : toggleBlock}
            blockBusy={blockBusy}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

